/**
 * Where *Undo This Import* runs (`equipment-page-undo-import`). A component rather than a hook in
 * the page, because the toast it raises needs `useToast`, and the page renders its own
 * `ToastProvider` — so only something mounted inside it can reach one. Keyed by twin by its host,
 * as `EquipmentIdentify` is, so nothing of one twin's undo outlives a switch to another (R15).
 *
 * It renders the confirmation and hands the buttons' state to its children.
 */
import React from 'react';
import { Button, Icon, useToast } from '@repo/design-system';

import { EquipmentImportUndoModal } from './EquipmentImportUndoModal';
import {
  useEquipmentImportUndo,
  type EquipmentImportUndo,
  type EquipmentImportUndoArgs,
} from './useEquipmentImportUndo';

export interface EquipmentImportUndoHostProps extends Omit<EquipmentImportUndoArgs, 'toast'> {
  children: (undo: EquipmentImportUndo) => React.ReactNode;
}

export const EquipmentImportUndoHost: React.FC<EquipmentImportUndoHostProps> = ({
  children,
  ...args
}) => {
  const toast = useToast();
  const undo = useEquipmentImportUndo({
    ...args,
    // A success toast with no action: an undo cannot be taken back (R19).
    toast: ({ title, detail }) =>
      toast.show({ tone: 'success', icon: <Icon name="check" />, title, detail }),
  });
  return (
    <>
      {children(undo)}
      {/* Mounted only while it asks: `Modal` hands focus back on unmount as it does on close. */}
      {undo.modal && (
        <EquipmentImportUndoModal
          confirmation={undo.modal}
          busy={undo.busy}
          onKeep={undo.keep}
          onUndo={undo.confirm}
          returnFocusTo={undo.openerRef}
        />
      )}
    </>
  );
};

/**
 * *Undo This Import* — the same button in the summary (R5, frame 04) and on the file card (R6): a
 * text button, size `sm`, `rotate-left`. `loading`, not `disabled`, while its own undo runs: a
 * disabled button drops focus to `<body>`, and `loading` stays focusable, sets `aria-busy` and
 * refuses activation (R8). Disabled while an upload is in flight.
 */
export const EquipmentImportUndoButton: React.FC<{ undo: EquipmentImportUndo }> = ({ undo }) => (
  <Button
    type="button"
    variant="text"
    size="sm"
    iconLeft={<Icon name="rotate-left" size="sm" />}
    loading={undo.busy}
    disabled={undo.disabled}
    onClick={event => undo.press(event.currentTarget)}
  >
    {undo.label}
  </Button>
);
